import { c as _c } from "react/compiler-runtime";
import { useLazyQuery } from '@apollo/client/react';
import { Avatar, Box, Center, ProgressCircle, Flex, Heading, HStack, Icon, IconButton, Link, Spinner, Stack, Text, useDisclosure, VStack, Wrap, Presence, AbsoluteCenter } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { useMatchingContext } from '@src/components/sco/Matching/Layout/MatchingContext';
import { UserMatchExplanation } from '@src/components/sco/Matching/UserMatchExplanation';
import { UserMatchRatingButtons } from '@src/components/sco/Matching/UserMatchRatingButtons';
import { UserMatchSaveButton } from '@src/components/sco/Matching/UserMatchSaveButton';
import { UserMatchSendMessageButton } from '@src/components/sco/Matching/UserMatchSendMessageButton';
import { USER_MATCH_FRAGMENT } from '@src/components/sco/Matching/userSearchFragment';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { ViewableTracker } from '@src/components/sco/ViewableTracker';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { SSRGate } from '@src/components/sco/utilities/SSRGate';
import useOnScreen from '@src/hooks/useOnScreen';
import { useUserAccessCheck } from '@src/hooks/useUserAccessCheck';
import { gql } from '@src/lib/gql';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useRef } from 'react';
import CountUp from 'react-countup';
import { FaBriefcase, FaLinkedin, FaMapMarkerAlt } from 'react-icons/fa';
export const WorkspaceUserMatchCard = (props: {
  delayLoading?: boolean;
  userMatchId: string;
  showUserSearchBanner?: boolean;
  isExpanded?: boolean;
  trackView?: boolean;
}) => {
  const matchingContext = useMatchingContext();
  const profileExpansion = useDisclosure({
    open: props.isExpanded
  });
  const cardRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(cardRef);
  const isOnScreenDelayed = useDebounce(isOnScreen, 200);
  const matchingAccessCheck = useUserAccessCheck({
    require: {
      contentPermissions: ['canAccessMemberMatching']
    }
  });
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const isMobile = getBreakpointValue([true, true, false, false]);
  const [runQuery, query] = useLazyQuery(gql`
      query($userMatchId: String!) {
        scoUserMatch(userMatchId: $userMatchId) {
          id
          ${USER_MATCH_FRAGMENT}
          ${props.showUserSearchBanner ? `
                userSearch {
                  id
                  title
                  userSearchId
                }
              ` : ''}

        }
      }
    `);
  const userMatch = query.data?.scoUserMatch;
  const targetUser = userMatch?.targetUser;
  useEffect(() => {
    if (!props.delayLoading) {
      runQuery({
        variables: {
          userMatchId: props.userMatchId
        }
      });
    }
  }, [props.delayLoading]);
  useEffect(() => {
    // Close expanded profile when this becomes inactive in the carousel
    // This fixes issue with carousel heights and scrolling to blank space
    if (userMatch?.userMatchId && profileExpansion.open && matchingContext.activeUserMatchId && matchingContext.activeUserMatchId !== userMatch?.userMatchId) {
      profileExpansion.onClose();
    }
  }, [userMatch?.userMatchId, matchingContext.activeUserMatchId]);
  if (!userMatch) {
    if (query.error) {
      return <Center padding={'1em'} width={'full'}>
          <Text>
            {'Error: '}
            {query.error.toString()}
          </Text>
        </Center>;
    }
    return <Center padding={'1em'} width={'full'}>
        <Spinner />
      </Center>;
  }
  if (props.delayLoading) {
    return <></>;
  }
  const targetUserDisplayName = matchingAccessCheck?.accessCheckPassed ? targetUser.fullName : [targetUser.firstName, targetUser.lastName ? targetUser.lastName[0] + '.' : null].filter(Boolean).join(' ');
  return <Box ref={cardRef} bgColor={'colorPalette.container'} border={'1px solid'} borderColor={'colorPalette.subtle'} borderRadius={getBreakpointValue(['lg', 'xl', '2xl', '2xl'])} boxShadow={'xs'} margin={'auto'} // IMPORTANT: this fixes scrolling inside carousel
  // maxWidth={'container.lg'}
  width={'full'}>
      {props.trackView && userMatch && <ViewableTracker trackScrolling={true} viewableId={userMatch.userMatchId} viewableType={'UserMatch'} />}
      {props.showUserSearchBanner && userMatch.userSearch && <Stack align={getBreakpointValue(['start', 'start', 'center'])} bgColor={'colorPalette.subtle/40'} borderTopRadius={getBreakpointValue(['lg', 'xl', '2xl', '2xl'])} direction={getBreakpointValue(['row', 'row', 'row'])} justify={getBreakpointValue(['space-between', 'center', 'center'])} padding={2} textAlign={getBreakpointValue(['start', 'center', 'center'])}>
          <Text color={'colorPalette.text'} textStyle={isMobile ? 'xs' : 'xs'}>
            {'Matched on '}
            <strong>"{userMatch.userSearch.title}"</strong>
          </Text>
          <Link asChild fontSize={isMobile ? 'xs' : 'xs'} fontWeight={'600'} variant={'underline'}>
            <NextLink href={`/members/matching/${userMatch.userSearch.userSearchId}`}>
              {isMobile ? 'View All' : 'View All Matches'}
            </NextLink>
          </Link>
        </Stack>}
      <Stack align={getBreakpointValue(['center', 'center', 'stretch', 'stretch'])} direction={getBreakpointValue(['column', 'column', 'row', 'row'])} gap={getBreakpointValue(['4', '4', '10', '10'])} paddingX={getBreakpointValue(['2', '4', '8', '8'])} paddingY={getBreakpointValue(['2', '4', '8', '8'])}>
        <VStack align={'stretch'} flex={2} gap={getBreakpointValue([2, 4, 8, 8])} justify={'flex-start'} padding={0}>
          <Stack align={getBreakpointValue(['flex-start', 'flex-start', 'center'])} direction={getBreakpointValue(['row', 'row', 'row'])} gap={4}>
            <Box display={getBreakpointValue(['none', 'none', 'block'])}>
              <UserAvatar user={targetUser} avatarProps={{
              height: getBreakpointValue(['40px', '60px', '120px']),
              width: getBreakpointValue(['40px', '60px', '120px'])
            }} />
            </Box>
            <VStack align={getBreakpointValue(['center', 'center', 'flex-start'])} bgColor={getBreakpointValue(['primaryAlpha.200', 'colorPalette.bg', 'transparent'])} borderRadius={getBreakpointValue(['md', 'xl', 'xl'])} gap={2} padding={getBreakpointValue([4, 4, 0])} width={'full'}>
              <HStack>
                <Box display={getBreakpointValue(['block', 'block', 'none'])}>
                  <UserAvatar user={targetUser} avatarProps={{
                  height: '1.25em',
                  width: '1.25em'
                }} />
                </Box>
                <Heading size={getBreakpointValue(['lg', '2xl', '2xl'])}>{targetUserDisplayName}</Heading>
              </HStack>
              {matchingAccessCheck.accessCheckPassed && <>
                  <Wrap gap={2}>
                    {targetUser.company.name && <HStack align={'center'} gap={1} justify={'center'}>
                        <Icon asChild boxSize={3} color={'colorPalette.fg/50'}>
                          <FaBriefcase />
                        </Icon>

                        <Text color={'colorPalette.fg/50'} textStyle={getBreakpointValue(['labelXs', 'labelXs', 'labelXs'])}>
                          {targetUser.company.name}
                        </Text>
                      </HStack>}
                    {targetUser.location && <HStack align={'center'} gap={1} justify={'center'}>
                        <Icon asChild boxSize={3} color={'colorPalette.fg/50'}>
                          <FaMapMarkerAlt />
                        </Icon>
                        <Text color={'colorPalette.fg/50'} textStyle={getBreakpointValue(['labelXs', 'labelXs', 'labelXs'])}>
                          {targetUser.location}
                        </Text>
                      </HStack>}
                  </Wrap>
                </>}

              <Wrap gap={2} marginTop={1}>
                <Flex>
                  <PaywalledAction contentPermission={'canAccessMemberMatching'} upgradeBoxProps={{
                  width: 'auto'
                }} upgradeCtaButtonProps={{
                  size: 'sm'
                }} upgradeCtaLabel={'Upgrade to Message'} variant={'button-only'}>
                    <UserMatchSendMessageButton buttonProps={{
                    size: getBreakpointValue(['xs', 'xs', 'xs'])
                  }} userMatchId={userMatch.userMatchId} />
                  </PaywalledAction>
                </Flex>
                <UserMatchSaveButton buttonProps={{
                size: getBreakpointValue(['xs', 'xs', 'xs'])
              }} userMatchId={userMatch.userMatchId} />
                {matchingAccessCheck.accessCheckPassed && targetUser.linkedin && <IconButton asChild aria-label={'LinkedIn'} size={getBreakpointValue(['xs', 'xs', 'xs'])} variant={'outline'}>
                    <NextLink href={targetUser.linkedin} target={'_blank'}>
                      <Icon asChild>
                        <FaLinkedin />
                      </Icon>
                    </NextLink>
                  </IconButton>}
              </Wrap>
            </VStack>
          </Stack>
          <Box display={getBreakpointValue(['block', 'block', 'none'])} width={'full'}>
            <ProfileCardMatchScore isOnScreen={isOnScreen} isOnScreenDelayed={isOnScreenDelayed} matchFactors={userMatch.matchFactors as [number, string, string, string][]} matchScoreAsPercent={userMatch.matchScoreAsPercent} matchScoreAsPoints={userMatch.matchScore} userMatch={userMatch} />
          </Box>
          <VStack bgColor={'colorPalette.card/25'} borderRadius={getBreakpointValue(['md', 'xl', 'xl'])} boxShadow={'sm'} gap={2} padding={4} width={'full'}>
            <HStack width={'full'}>
              <Avatar.Root bgColor={'colorPalette.border'} size={'xs'}>
                <Avatar.Fallback name={'Startups.com Advisor AI'} />
              </Avatar.Root>
              <Text color={'colorPalette.fg'} fontSize={'xs'} fontWeight={'semibold'} opacity={1}>
                Startups.com Advisor AI
              </Text>
            </HStack>
            <SSRGate disableSSR={true}>
              <UserMatchExplanation effect={'collapse'} preloadedUserMatchExplanation={userMatch.matchExplanation} userMatchId={userMatch.userMatchId} />
            </SSRGate>
          </VStack>
        </VStack>
        <Box display={getBreakpointValue(['none', 'none', 'block'])} minWidth={getBreakpointValue(['full', 'full', 'xs', 'xs'])} width={getBreakpointValue(['full', 'full', 'auto', 'auto'])}>
          <ProfileCardMatchScore isOnScreen={isOnScreen} isOnScreenDelayed={isOnScreenDelayed} matchFactors={userMatch.matchFactors as [number, string, string, string][]} matchScoreAsPercent={userMatch.matchScoreAsPercent} matchScoreAsPoints={userMatch.matchScore} userMatch={userMatch} />
        </Box>
      </Stack>
    </Box>;
};
export const ProfileCardMatchScore = props => {
  const $ = _c(44);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  getBreakpointValue([true, true, false, false]);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      _light: "colorPalette.container",
      _dark: "colorPalette.solid/50"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== getBreakpointValue) {
    t1 = getBreakpointValue(["md", "xl", "xl"]);
    $[1] = getBreakpointValue;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== getBreakpointValue) {
    t2 = getBreakpointValue(["row", "row", "column"]);
    $[3] = getBreakpointValue;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== getBreakpointValue) {
    t3 = getBreakpointValue([2, 4, 4]);
    $[5] = getBreakpointValue;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props.isOnScreen || $[8] !== props.matchScoreAsPercent || $[9] !== props.matchScoreAsPoints) {
    t4 = <ProfileCardAnimatedMatchScore in={props.isOnScreen} matchScoreAsPercent={props.matchScoreAsPercent} matchScoreAsPoints={props.matchScoreAsPoints} />;
    $[7] = props.isOnScreen;
    $[8] = props.matchScoreAsPercent;
    $[9] = props.matchScoreAsPoints;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <VStack align="center" padding={t3} width="full">{t4}</VStack>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== getBreakpointValue) {
    t6 = getBreakpointValue(["none", "none", "2xl"]);
    $[14] = getBreakpointValue;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== getBreakpointValue) {
    t7 = getBreakpointValue([2, 4, 4]);
    $[16] = getBreakpointValue;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== props.isOnScreen || $[19] !== props.isOnScreenDelayed || $[20] !== props.matchFactors) {
    t8 = props.matchFactors?.map((t9, index) => {
      const [points,, title] = t9;
      return <Presence key={index} animationDuration="moderate" present={props.isOnScreenDelayed} animationStyle={{
        _open: "scale-fade-in",
        _closed: "scale-fade-out"
      }}><Text key={index} animation={props.isOnScreen ? "2s ease-in-out bgOpacity" : "none"} bgColor="colorPalette.fg/10" borderRadius="md" color="colorPalette.text" padding={2} textAlign="center" textStyle="label2xs" css={{
          "--bg-opacity-from": "var(--chakra-colors-colorPalette-solid-20)",
          "--bg-opacity-to": "var(--chakra-colors-colorPalette-solid-40)"
        }}>{title}{" "}<span style={{
            fontSize: "0.8em",
            marginLeft: "0.5em",
            backgroundColor: "var(--chakra-colors-colorPalette-solid-30)",
            padding: "0.25em",
            borderRadius: "0.25em"
          }}>+{points}pts</span></Text></Presence>;
    });
    $[18] = props.isOnScreen;
    $[19] = props.isOnScreenDelayed;
    $[20] = props.matchFactors;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <VStack align="stretch" padding={t7} width="full">{t8}</VStack>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== getBreakpointValue) {
    t10 = getBreakpointValue(["column", "row"]);
    $[25] = getBreakpointValue;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== getBreakpointValue) {
    t11 = getBreakpointValue([2, 4, 4]);
    $[27] = getBreakpointValue;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== props.userMatch) {
    t12 = <UserMatchRatingButtons userMatch={props.userMatch} />;
    $[29] = props.userMatch;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12) {
    t13 = <Stack align="stretch" bgColor="colorPalette.bg/50" direction={t10} padding={t11} width="full">{t12}</Stack>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13 || $[36] !== t6 || $[37] !== t9) {
    t14 = <Stack align="center" alignSelf="stretch" bgColor="colorPalette.container" borderTopRadius={t6} boxShadow="sm" flexWrap="wrap" gap={0} justify="space-between" padding={0} w="100%">{t9}{t13}</Stack>;
    $[35] = t13;
    $[36] = t6;
    $[37] = t9;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t1 || $[40] !== t14 || $[41] !== t2 || $[42] !== t5) {
    t15 = <Stack align="center" bgColor={t0} borderRadius={t1} boxShadow="md" direction={t2} flex={1} gap={0} overflow="hidden" width="full">{t5}{t14}</Stack>;
    $[39] = t1;
    $[40] = t14;
    $[41] = t2;
    $[42] = t5;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
};
export const ProfileCardAnimatedMatchScore = props => {
  const $ = _c(32);
  const onScreenRef = useRef(null);
  const debouncedShowScoreFirst = useDebounce(props.in, 100);
  const debouncedShowScoreSecond = useDebounce(props.in, 150);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t0;
  if ($[0] !== getBreakpointValue) {
    t0 = getBreakpointValue(["xl", "xl", "8xl"]);
    $[0] = getBreakpointValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== debouncedShowScoreSecond || $[3] !== props.matchScoreAsPercent) {
    t1 = debouncedShowScoreSecond ? Math.round(props.matchScoreAsPercent || 1) : 1;
    $[2] = debouncedShowScoreSecond;
    $[3] = props.matchScoreAsPercent;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      "& circle": {
        transition: "1.5s cubic-bezier(0.33, 1, 0.68, 1)"
      }
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ProgressCircle.Circle><ProgressCircle.Track /><ProgressCircle.Range stroke="colorPalette.border" strokeLinecap="round" /></ProgressCircle.Circle>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== getBreakpointValue) {
    t4 = getBreakpointValue(["none", "none", "block", "block"]);
    $[7] = getBreakpointValue;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <Text color="colorPalette.border" display={t4} textStyle="label">Score</Text>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== getBreakpointValue) {
    t6 = getBreakpointValue(["xl", "xl", "2xl", "2xl"]);
    $[11] = getBreakpointValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== debouncedShowScoreFirst || $[14] !== props.matchScoreAsPercent) {
    t7 = debouncedShowScoreFirst ? <CountUp duration={1.5} end={Math.round(props.matchScoreAsPercent)} start={0} easingFn={_temp} /> : "0";
    $[13] = debouncedShowScoreFirst;
    $[14] = props.matchScoreAsPercent;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <Heading lineHeight="100%" size={t6}>{t7}%</Heading>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== getBreakpointValue) {
    t9 = getBreakpointValue(["none", "none", "block", "block"]);
    $[19] = getBreakpointValue;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== props.matchScoreAsPoints || $[22] !== t9) {
    t10 = <Text color="colorPalette.fg/50" display={t9} fontSize="xs" fontWeight="semibold" textAlign="center">+{props.matchScoreAsPoints}pts</Text>;
    $[21] = props.matchScoreAsPoints;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t5 || $[26] !== t8) {
    t11 = <AbsoluteCenter><VStack gap={1}>{t5}{t8}{t10}</VStack></AbsoluteCenter>;
    $[24] = t10;
    $[25] = t5;
    $[26] = t8;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t0 || $[29] !== t1 || $[30] !== t11) {
    t12 = <Box ref={onScreenRef}><ProgressCircle.Root colorPalette="teal" size={t0} value={t1} css={t2}>{t3}{t11}</ProgressCircle.Root></Box>;
    $[28] = t0;
    $[29] = t1;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
function _temp(t, b, c, d) {
  return c * ((t = t / d - 1) * t * t + 1) + b;
}